import { IScenarioFormField, ScenarioFormFieldType } from "../../models/ILiveChatScenario";
import { FIELD_TYPES, toFieldName } from "../../utils/scenarioUtils";

interface Props {
  fields: IScenarioFormField[];
  onChange: (fields: IScenarioFormField[]) => void;
}

export default function FormFieldsEditor({ fields, onChange }: Props) {
  const update = (index: number, patch: Partial<IScenarioFormField>) =>
    onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  const changeLabel = (index: number, label: string) => {
    const f = fields[index];
    // Anahtar elle değiştirilmediyse etiketle birlikte güncellensin
    const autoName = !f.name || f.name === toFieldName(f.label);
    update(index, autoName ? { label, name: toFieldName(label) } : { label });
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = fields.slice();
    const [item] = next.splice(index, 1);
    next.splice(index + dir, 0, item);
    onChange(next);
  };

  const add = () => onChange([...fields, { name: "", label: "", type: "text", required: true }]);

  const nameCounts = fields.reduce<Record<string, number>>((acc, f) => {
    if (f.name) acc[f.name] = (acc[f.name] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {fields.length === 0 && (
        <div className="lcs-note lcs-note--warn">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <span>Form henüz hiç alan sormuyor. Müşteriden bilgi almak için alan ekleyin.</span>
        </div>
      )}

      {fields.map((f, i) => {
        const duplicate = !!f.name && nameCounts[f.name] > 1;
        return (
          <div key={i} className="lcs-field-card">
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: ".7rem", fontWeight: 800, color: "#7c3aed", background: "#f3e8ff", borderRadius: 6, padding: "2px 7px" }}>{i + 1}</span>
              <input className="lcs-input" style={{ flex: 1, padding: "7px 10px" }} value={f.label} maxLength={120}
                onChange={(e) => changeLabel(i, e.target.value)} placeholder="Müşterinin göreceği etiket (ör. Restoran adı)" />
              <button type="button" className="lcs-icon-btn" title="Yukarı" disabled={i === 0} onClick={() => move(i, -1)}><i className="bi bi-arrow-up"></i></button>
              <button type="button" className="lcs-icon-btn" title="Aşağı" disabled={i === fields.length - 1} onClick={() => move(i, 1)}><i className="bi bi-arrow-down"></i></button>
              <button type="button" className="lcs-icon-btn lcs-icon-btn--danger" title="Alanı sil" onClick={() => onChange(fields.filter((_, j) => j !== i))}><i className="bi bi-trash3"></i></button>
            </div>

            <div className="lcs-grid-4" style={{ alignItems: "end" }}>
              <div>
                <div className="lcs-label">Tür</div>
                <select className="lcs-input" style={{ padding: "7px 8px" }} value={f.type}
                  onChange={(e) => update(i, { type: e.target.value as ScenarioFormFieldType })}>
                  {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <div className="lcs-label">Anahtar</div>
                <input className="lcs-input lcs-input--mono" style={{ padding: "7px 8px", borderColor: duplicate ? "#f87171" : undefined }}
                  value={f.name} onChange={(e) => update(i, { name: e.target.value.replace(/\s/g, "") })} placeholder="restaurantName" />
              </div>
              <div>
                <div className="lcs-label">En fazla</div>
                <input type="number" min={1} max={500} className="lcs-input" style={{ padding: "7px 8px" }}
                  value={f.maxLength ?? ""} placeholder="120"
                  onChange={(e) => update(i, { maxLength: e.target.value ? Number(e.target.value) : undefined })} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: ".78rem", fontWeight: 700, color: "#374151", cursor: "pointer", paddingBottom: 9 }}>
                <input type="checkbox" checked={!!f.required} onChange={(e) => update(i, { required: e.target.checked })} />
                Zorunlu
              </label>
            </div>

            {duplicate && <div className="lcs-hint" style={{ color: "#dc2626" }}>Bu anahtar başka bir alanda da kullanılıyor.</div>}

            {f.type === "select" && (
              <div style={{ marginTop: 8 }}>
                <div className="lcs-label">Seçenekler <span style={{ fontWeight: 500, color: "#9ca3af" }}>her satıra bir seçenek</span></div>
                <textarea className="lcs-input" rows={3} value={(f.options ?? []).join("\n")}
                  onChange={(e) => update(i, { options: e.target.value.split("\n") })}
                  placeholder={"Lefkoşa\nGirne\nGazimağusa"} />
              </div>
            )}
          </div>
        );
      })}

      <button type="button" className="lcs-btn" style={{ borderStyle: "dashed", color: "#7c3aed", borderColor: "#d8b4fe" }} onClick={add}>
        <i className="bi bi-plus-lg"></i>Alan ekle
      </button>
    </div>
  );
}
